import React, { FC, ReactNode, useCallback } from 'react';
import cx from 'classnames';
import Clickable from '@move-ui/clickable';
import { MaskedLink } from '@move-web-essentials-utils/masked-link';

import Icon, { MenuIcon } from '../Icon';
import { useTheme } from '../../utils/theme';
import { useTranslation, useCurrentLocale } from '../../utils/translations';
import getLocalizedUrl from '../../utils/urls';
import styles from './MaskedExternalLink.css';

type Props = {
  className?: string;
  children?: ReactNode;
  icon?: string | MenuIcon | ReactNode;
  url: string;
  dataTestId?: string;
  label?: string;
  name: string;
  onClick?: (
    name: string,
    details?: { [key: string]: string | undefined },
    event?: Event
  ) => void;
};

const MaskedExternalLink: FC<Props> = ({
  className,
  children,
  icon,
  url,
  dataTestId,
  label,
  name,
  onClick
}): JSX.Element => {
  const locale = useCurrentLocale();
  const { isBranded, isTakeover } = useTheme();
  const { translate } = useTranslation();

  const localizedUrl = getLocalizedUrl(url, locale);

  const handleClick = useCallback(
    (event) => onClick?.(name, { type: 'masked', url: localizedUrl }, event),
    [name, url, onClick]
  );

  const text = label ? translate(label) : null;

  return (
    <MaskedLink target="_blank" url={url}>
      <Clickable
        className={cx(className, styles.component)}
        role="menuitem"
        data-testid={dataTestId}
        onClick={handleClick}
      >
        {icon && <Icon className={styles.icon} icon={icon} />}
        {text && (
          <span
            className={cx(styles.text, {
              [styles.brandedText]: isBranded,
              [styles.takeoverText]: isTakeover
            })}
          >
            {text}
          </span>
        )}
        {children}
      </Clickable>
    </MaskedLink>
  );
};

export default MaskedExternalLink;
